/**
 * schedules.ts
 * CRUD service for Autonomous Schedules & Persistent Workflows.
 * Mirrors the pattern established in chat.ts — direct Supabase calls.
 */

import { supabase } from '../lib/supabaseClient';
import { ScheduledWorkflow, WorkflowStep, WorkflowStatus } from '../types';
import { isAuthMockMode } from './auth';

// ─── Mock Data (for auth mock mode) ───
let mockSchedules: ScheduledWorkflow[] = [];

// ─── Helpers ───

/** Compute the next run time from a cron expression (simple approximation for UI display) */
const computeNextRun = (cron: string): string => {
  // This is a simplified client-side estimate.
  // The backend worker will compute the exact next run.
  const now = new Date();
  const parts = cron.split(' ');
  if (parts.length !== 5) return now.toISOString();

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  const next = new Date(now);

  // Handle hourly pattern (e.g. "0 * * * *")
  if (minute !== '*' && hour === '*') {
    next.setMinutes(parseInt(minute, 10), 0, 0);
    if (next <= now) {
      next.setHours(next.getHours() + 1);
    }
  }
  // Handle simple daily/weekly patterns
  else if (hour !== '*' && minute !== '*') {
    next.setHours(parseInt(hour, 10), parseInt(minute, 10), 0, 0);
    if (next <= now) {
      if (dayOfWeek !== '*') {
        // Weekly — advance to next matching day
        const targetDay = parseInt(dayOfWeek, 10);
        let daysUntil = targetDay - now.getDay();
        if (daysUntil <= 0) daysUntil += 7;
        next.setDate(next.getDate() + daysUntil);
      } else {
        // Daily — advance to tomorrow
        next.setDate(next.getDate() + 1);
      }
    }
  }

  return next.toISOString();
};

// ─── Public API ───

/** Fetch all workflows for the current user, including their steps */
export const getSchedules = async (): Promise<ScheduledWorkflow[]> => {
  if (isAuthMockMode) return [...mockSchedules];

  const { data: workflows, error } = await supabase!
    .from('scheduled_workflows')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[Schedules] Failed to fetch workflows:', error);
    return [];
  }

  if (!workflows || workflows.length === 0) return [];

  // Batch-fetch all steps for the returned workflows
  const workflowIds = workflows.map((w: any) => w.id);
  const { data: allSteps } = await supabase!
    .from('workflow_steps')
    .select('*')
    .in('workflow_id', workflowIds)
    .order('step_order', { ascending: true });

  const stepsByWorkflow = new Map<string, WorkflowStep[]>();
  for (const step of (allSteps || [])) {
    const list = stepsByWorkflow.get(step.workflow_id) || [];
    list.push({
      id: step.id,
      workflow_id: step.workflow_id,
      step_order: step.step_order,
      tool_name: step.tool_name,
      args: step.args || {},
      created_at: step.created_at,
    });
    stepsByWorkflow.set(step.workflow_id, list);
  }

  return workflows.map((w: any) => ({
    id: w.id,
    user_id: w.user_id,
    name: w.name,
    description: w.description,
    type: w.type,
    cron_expression: w.cron_expression,
    status: w.status,
    last_run_at: w.last_run_at,
    next_run_at: w.next_run_at,
    created_at: w.created_at,
    steps: stepsByWorkflow.get(w.id) || [],
  }));
};

/** Fetch a single workflow by ID with its steps */
export const getScheduleById = async (id: string): Promise<ScheduledWorkflow | null> => {
  if (isAuthMockMode) return mockSchedules.find(s => s.id === id) || null;

  const [workflowResult, stepsResult] = await Promise.all([
    supabase!.from('scheduled_workflows').select('*').eq('id', id).single(),
    supabase!.from('workflow_steps').select('*').eq('workflow_id', id).order('step_order', { ascending: true }),
  ]);

  if (workflowResult.error || !workflowResult.data) return null;

  const w = workflowResult.data;
  return {
    id: w.id,
    user_id: w.user_id,
    name: w.name,
    description: w.description,
    type: w.type,
    cron_expression: w.cron_expression,
    status: w.status,
    last_run_at: w.last_run_at,
    next_run_at: w.next_run_at,
    created_at: w.created_at,
    steps: (stepsResult.data || []).map((s: any) => ({
      id: s.id,
      workflow_id: s.workflow_id,
      step_order: s.step_order,
      tool_name: s.tool_name,
      args: s.args || {},
      created_at: s.created_at,
    })),
  };
};

/** Create a new scheduled workflow with its steps */
export const createSchedule = async (data: {
  name: string;
  description?: string;
  type: 'cron' | 'webhook' | 'event';
  cron_expression?: string;
  steps: { tool_name: string; args: Record<string, any> }[];
}): Promise<ScheduledWorkflow | null> => {
  if (isAuthMockMode) {
    const mock: ScheduledWorkflow = {
      id: 'mock-' + Date.now(),
      user_id: 'mock-user',
      name: data.name,
      description: data.description,
      type: data.type,
      cron_expression: data.cron_expression,
      status: 'active',
      next_run_at: data.cron_expression ? computeNextRun(data.cron_expression) : undefined,
      created_at: new Date().toISOString(),
      steps: data.steps.map((s, i) => ({
        id: `mock-step-${Date.now()}-${i}`,
        workflow_id: 'mock-' + Date.now(),
        step_order: i + 1,
        tool_name: s.tool_name,
        args: s.args,
        created_at: new Date().toISOString(),
      })),
    };
    mockSchedules = [mock, ...mockSchedules];
    return mock;
  }

  const { data: { user } } = await supabase!.auth.getUser();
  if (!user) throw new Error('Authentication required');

  // Insert workflow
  const { data: workflow, error: wError } = await supabase!
    .from('scheduled_workflows')
    .insert({
      user_id: user.id,
      name: data.name,
      description: data.description,
      type: data.type,
      cron_expression: data.cron_expression,
      status: 'active',
      next_run_at: data.cron_expression ? computeNextRun(data.cron_expression) : null,
    })
    .select()
    .single();

  if (wError || !workflow) {
    console.error('[Schedules] Failed to create workflow:', wError);
    throw new Error(wError?.message || 'Failed to create workflow');
  }

  // Insert steps
  const stepsToInsert = data.steps.map((s, i) => ({
    workflow_id: workflow.id,
    step_order: i + 1,
    tool_name: s.tool_name,
    args: s.args,
  }));

  const { data: steps, error: sError } = await supabase!
    .from('workflow_steps')
    .insert(stepsToInsert)
    .select();

  if (sError) {
    console.warn('[Schedules] Failed to insert steps:', sError);
  }

  return {
    ...workflow,
    steps: (steps || []).map((s: any) => ({
      id: s.id,
      workflow_id: s.workflow_id,
      step_order: s.step_order,
      tool_name: s.tool_name,
      args: s.args || {},
      created_at: s.created_at,
    })),
  };
};

/** Update a workflow's status (pause / resume / deactivate) */
export const updateScheduleStatus = async (id: string, status: WorkflowStatus): Promise<void> => {
  if (isAuthMockMode) {
    mockSchedules = mockSchedules.map(s => s.id === id ? { ...s, status } : s);
    return;
  }

  const { error } = await supabase!
    .from('scheduled_workflows')
    .update({ status })
    .eq('id', id);

  if (error) {
    console.error('[Schedules] Failed to update status:', error);
    throw new Error(error.message);
  }
};

/** Delete a workflow (steps cascade automatically) */
export const deleteSchedule = async (id: string): Promise<void> => {
  if (isAuthMockMode) {
    mockSchedules = mockSchedules.filter(s => s.id !== id);
    return;
  }

  const { error } = await supabase!
    .from('scheduled_workflows')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[Schedules] Failed to delete workflow:', error);
    throw new Error(error.message);
  }
};

/** Update a workflow's name, description, cron, or steps. Returns the new next run time if the cron changed. */
export const updateSchedule = async (id: string, data: {
  name?: string;
  description?: string;
  cron_expression?: string;
  steps?: { tool_name: string; args: Record<string, any> }[];
}): Promise<string | undefined> => {
  if (isAuthMockMode) {
    let nextRun: string | undefined = undefined;
    mockSchedules = mockSchedules.map(s => {
      if (s.id !== id) return s;
      nextRun = data.cron_expression ? computeNextRun(data.cron_expression) : s.next_run_at;
      return {
        ...s,
        name: data.name || s.name,
        description: data.description ?? s.description,
        cron_expression: data.cron_expression ?? s.cron_expression,
        next_run_at: nextRun,
      };
    });
    return nextRun;
  }

  // Update workflow metadata
  const updatePayload: any = {};
  if (data.name) updatePayload.name = data.name;
  if (data.description !== undefined) updatePayload.description = data.description;
  let nextRun: string | undefined = undefined;
  if (data.cron_expression !== undefined) {
    updatePayload.cron_expression = data.cron_expression;
    nextRun = computeNextRun(data.cron_expression);
    updatePayload.next_run_at = nextRun;
    updatePayload.status = 'active'; // Automatically reactivate on schedule change
  }

  if (Object.keys(updatePayload).length > 0) {
    const { error } = await supabase!
      .from('scheduled_workflows')
      .update(updatePayload)
      .eq('id', id);
    if (error) throw new Error(error.message);
  }

  // Replace steps if provided
  if (data.steps) {
    // Delete existing steps
    await supabase!.from('workflow_steps').delete().eq('workflow_id', id);

    // Insert new steps
    const stepsToInsert = data.steps.map((s, i) => ({
      workflow_id: id,
      step_order: i + 1,
      tool_name: s.tool_name,
      args: s.args,
    }));

    const { error: sError } = await supabase!
      .from('workflow_steps')
      .insert(stepsToInsert);

    if (sError) throw new Error(sError.message);
  }

  return nextRun;
};

/** Delete a specific execution log from all steps of a workflow */
export const deleteWorkflowLog = async (workflowId: string, runId: string, steps: any[]): Promise<void> => {
  if (isAuthMockMode) {
    mockSchedules = mockSchedules.map(w => {
      if (w.id !== workflowId) return w;
      return {
        ...w,
        steps: (w.steps || []).map(step => {
          const args = { ...(step.args || {}) };
          if (args.execution_history) {
            args.execution_history = args.execution_history.filter((h: any) => h.run_id !== runId);
          }
          if (runId.startsWith('fallback-')) {
            delete args.last_result;
          }
          return { ...step, args };
        })
      };
    });
    return;
  }

  for (const step of steps) {
    const args = { ...(step.args || {}) };
    const history = args.execution_history || [];
    const newHistory = history.filter((h: any) => h.run_id !== runId);
    
    const updatedArgs = {
      ...args,
      execution_history: newHistory,
    };
    
    if (runId.startsWith('fallback-')) {
      delete updatedArgs.last_result;
    }

    const { error } = await supabase!
      .from('workflow_steps')
      .update({ args: updatedArgs })
      .eq('id', step.id);

    if (error) {
      console.error(`[Schedules] Failed to delete run log for step ${step.id}:`, error);
      throw new Error(error.message);
    }
  }
};
